/**
 * Request Blocker for demo.optimoteams.com
 *
 * This utility blocks ALL backend API requests to prevent
 * errors when running without a backend server.
 *
 * Blocks:
 * - localhost:8000
 * - /optimo/api
 * - /api/v1/
 *
 * Also clears browser storage to remove auth tokens.
 */

const BLOCKED_PATTERNS = [
    'localhost:8000',
    '127.0.0.1:8000',
    '/optimo/api',
    '/api/v1/',
]

let isInitialized = false

const shouldBlockRequest = (url: string): boolean => {
    return BLOCKED_PATTERNS.some((pattern) => url.includes(pattern))
}

export const initializeRequestBlocker = (): void => {
    if (isInitialized) {
        console.log('[SECURITY] Backend request blocker already initialized')
        return
    }

    console.log('[SECURITY] Initializing backend request blocker for demo.optimoteams.com')

    // Block fetch requests
    const originalFetch = window.fetch
    window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
        const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url

        if (shouldBlockRequest(url)) {
            console.warn(`[BLOCKED] Fetch request to: ${url}`)
            return Promise.reject(
                new Error(
                    `Backend requests are disabled in demo mode. Attempted request to: ${url}`,
                ),
            )
        }

        return originalFetch(input, init)
    }

    // Block XMLHttpRequest
    const originalOpen = XMLHttpRequest.prototype.open
    XMLHttpRequest.prototype.open = function (
        method: string,
        url: string | URL,
        async?: boolean,
        username?: string | null,
        password?: string | null,
    ) {
        const urlString = typeof url === 'string' ? url : url.href

        if (shouldBlockRequest(urlString)) {
            console.warn(`[BLOCKED] XMLHttpRequest to: ${urlString}`)
            throw new Error(
                `Backend requests are disabled in demo mode. Attempted request to: ${urlString}`,
            )
        }

        return originalOpen.call(this, method, url, async ?? true, username, password)
    }

    // Clear browser storage to remove auth tokens that might trigger requests
    try {
        localStorage.removeItem('optimo_access_token')
        localStorage.removeItem('optimo_refresh_token')
        sessionStorage.clear()
        console.log('[SECURITY] Cleared auth tokens from storage')
    } catch (error) {
        console.warn('[SECURITY] Could not clear storage:', error)
    }

    isInitialized = true
    console.log('[SECURITY] Backend request blocker initialized successfully')
}
